import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { TranslationProvider, useTranslation } from '@ibk/shared-translation';
import { useCompanySelection } from '../hooks/useCompanySelection';
import { sharedUiTranslations } from '../translations';
import { CompanyList } from './CompanyList';
import styles from './CompanySelection.module.css';
import { CompanySelectionFooter } from './CompanySelectionFooter';
import { CompanySelectionLayout } from './CompanySelectionLayout';
import { Button, Card } from './index';
const CompanySelectionBase = ({ companies, loginBg, waypointBg, logoType, onSelectCompany, onGoBack, initialCompanyId, className = '', footerLinks, title, submitButtonText, backButtonText, disabled = false, 'data-testid': dataTestId, }) => {
    const { t } = useTranslation();
    const { selectedCompany, setSelectedCompany, hasSelection, handleSelectCompany, handleGoBack } = useCompanySelection({
        companies,
        onSelectCompany,
        onGoBack,
        initialCompanyId,
    });
    const cardRef = React.useRef(null);
    const isKeyboardNavigationRef = React.useRef(false);
    React.useEffect(() => {
        cardRef.current?.focus();
    }, []);
    React.useEffect(() => {
        if (isKeyboardNavigationRef.current && selectedCompany) {
            const companyListTestId = dataTestId ? `${dataTestId}-company-list` : 'company-list';
            const selectedElement = document.querySelector(`[data-testid="${companyListTestId}"] [data-selected="true"]`);
            if (selectedElement) {
                selectedElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
            isKeyboardNavigationRef.current = false;
        }
    }, [selectedCompany, dataTestId]);
    const handleArrowNavigation = (key) => {
        if (companies.length === 0) {
            return;
        }
        const currentIndex = selectedCompany ? companies.findIndex(c => c.id === selectedCompany) : -1;
        const isArrowDown = key === 'ArrowDown';
        let nextIndex;
        if (isArrowDown) {
            nextIndex = currentIndex < companies.length - 1 ? currentIndex + 1 : 0;
        }
        else {
            nextIndex = currentIndex > 0 ? currentIndex - 1 : companies.length - 1;
        }
        isKeyboardNavigationRef.current = true;
        setSelectedCompany(companies[nextIndex].id);
    };
    const handleKeyDown = (event) => {
        if (event.target.tagName === 'BUTTON') {
            return;
        }
        if (event.key === 'Enter' && hasSelection && !disabled) {
            event.preventDefault();
            handleSelectCompany();
            return;
        }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            handleArrowNavigation(event.key);
        }
    };
    const handleSubmit = (event) => {
        event.preventDefault();
        if (hasSelection && !disabled) {
            handleSelectCompany();
        }
    };
    return (_jsxs(CompanySelectionLayout, { loginBg: loginBg, waypointBg: waypointBg, logoType: logoType, className: className, "data-testid": dataTestId ? `${dataTestId}-layout` : undefined, children: [_jsx(Card, { className: styles.companySelectionCardWrapper, "data-testid": dataTestId ? `${dataTestId}-card` : undefined, children: _jsxs("form", { ref: cardRef, className: styles.companySelectionForm, "data-testid": dataTestId ? `${dataTestId}-form` : 'card', tabIndex: 0, onKeyDown: handleKeyDown, onSubmit: handleSubmit, "aria-label": title || t('company-selection.title'), children: [companies.length > 0 && (_jsx("div", { className: styles.companySelectionFormTitle, "data-testid": dataTestId ? `${dataTestId}-title` : undefined, children: title })), _jsx(CompanyList, { companies: companies, selectedCompany: selectedCompany, onSelectionChange: setSelectedCompany, disabled: disabled, "data-testid": dataTestId ? `${dataTestId}-company-list` : undefined }), _jsxs("div", { className: styles.companySelectionButtons, children: [companies.length > 0 && (_jsx(Button, { variant: 'primary', size: 'large', type: 'submit', disabled: !hasSelection || disabled, dataTestId: dataTestId ? `${dataTestId}-submit-button` : 'submit-button', className: styles.button, children: submitButtonText })), _jsx(Button, { type: 'button', variant: 'tertiary', size: 'large', onClick: handleGoBack, disabled: disabled, dataTestId: dataTestId ? `${dataTestId}-back-button` : 'back-button', className: styles.button, children: companies.length > 0 ? backButtonText : t('no-accounts-error.go-back-to-login') })] })] }) }), _jsx(CompanySelectionFooter, { links: footerLinks, "data-testid": dataTestId ? `${dataTestId}-footer` : undefined })] }));
};
export const CompanySelection = props => {
    return (_jsx(TranslationProvider, { translations: sharedUiTranslations, defaultLanguage: 'pt-BR', children: _jsx(CompanySelectionBase, { ...props }) }));
};
